import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import exportIcon from "../assets/export.svg";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import pdfIcon from "../assets/pdf.svg";

function OpdPaymentTables({ data, setIsLoading, isLoading, filters }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);

    // Calculate totals
    const totals = data?.reduce((acc, curr) => ({
        amount: (acc.amount || 0) + curr.amount,
        discount: (acc.discount || 0) + (curr.discount_amount || 0),
    }), {});

    // Pagination
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = data?.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil((data?.length || 0) / recordsPerPage);

    const handleExport = () => {
        setIsLoading(true);

        const exportData = data?.map(item => ({
            'Date': item.Date.split('T')[0].split('-').reverse().join('-'),
            'Doctor Name': item.DoctorName,
            'Referred Doctor': item.ReferredDoctorName,
            'Patient Id': item.patientId,
            'Patient Name': `${item.PatientName} (${item.patientId})`,
            'Location': item.location,
            'Charged': item.amount + item.discount_amount,
            'Discount': item.discount_amount || 0,
            'Collected': item.amount || 0,
            'Pending': 0
        })) || [];

        // Calculate totals
        const totals = data?.reduce((acc, curr) => ({
            charged: (acc.charged || 0) + (curr.amount + curr.discount_amount),
            discount: (acc.discount || 0) + (curr.discount_amount || 0),
            collected: (acc.collected || 0) + (curr.amount || 0),
            balance: 0
        }), {});

        // Add totals row
        if (totals) {
            exportData.push({
                'Date': 'TOTAL',
                'Doctor Name': '',
                'Referred Doctor': '',
                'Patient Id': '',
                'Patient Name': '',
                'Location': '',
                'Charged': totals.charged,
                'Discount': totals.discount,
                'Collected': totals.collected,
                'Pending': totals.balance
            });
        }

        // Create a worksheet
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Function to calculate column widths dynamically
        const getColumnWidths = (data) => {
            if (!data.length) return [];

            return Object.keys(data[0]).map((key) => ({
                wch: Math.max(
                    key.length, // Header length
                    ...data.map(row => (row[key] ? row[key].toString().length : 0)) // Max content length
                ) + 2 // Add padding
            }));
        };

        // Set auto-adjusting column widths
        ws['!cols'] = getColumnWidths(exportData);

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Patient Transactions');

        // Save file
        XLSX.writeFile(
            wb,
            `OPD_${data[0].DoctorName}_${data[0].location}_${filters.startDate}_To_${filters.endDate}.xlsx`
        );

        setIsLoading(false);
    };

    const handleRecordsPerPageChange = (e) => {
        setRecordsPerPage(Number(e.target.value));
        setCurrentPage(1); // Reset to first page on change
      };
      const handleExportPDF = () => {
        setIsLoading(true); // Show loading icon
      
        const formatDate = (date) =>
          date ? date.split("T")[0].split("-").reverse().join("-") : "";
      
        const modifiedData = data.map((item) => {
          const charged = (item.amount || 0) + (item.discount_amount || 0);
          const collected = item.amount || 0;
          const discount = item.discount_amount || 0;
          const pending = 0;
      
          return {
            Date: formatDate(item.Date),
            "Doctor Name": item.DoctorName,
            "Referred Doctor": item.ReferredDoctorName,
            "Patient Id": item.patientId,
            "Patient Name": `${item.PatientName} (${item.patientId})`,
            Location: item.location,
            Charged: charged,
            Discount: discount,
            Collected: collected,
            Pending: pending,
          };
        });
      
        // Totals
        const totalCharged = modifiedData.reduce((sum, item) => sum + item.Charged, 0);
        const totalDiscount = modifiedData.reduce((sum, item) => sum + item.Discount, 0);
        const totalCollected = modifiedData.reduce((sum, item) => sum + item.Collected, 0);
        const totalPending = modifiedData.reduce((sum, item) => sum + item.Pending, 0);
      
        const doc = new jsPDF({ orientation: "landscape" });
      
        // Title
        doc.setFontSize(14);
        doc.text("Doctor OPD Report", 14, 15);
        doc.text(`Date From: ${formatDate(filters?.startDate)} --- Date To: ${formatDate(filters?.endDate)}`, 14, 25);
        doc.text(`Generated On: ${new Date().toLocaleString()}`, 14, 35);
      
        const columns = [
          "Date",
          "Doctor Name",
          "Referred Doctor",
          "Patient Id",
          "Patient Name",
          "Location",
          "Charged",
          "Discount",
          "Collected",
          "Pending",
        ];
      
        const rows = modifiedData.map((item) => Object.values(item));
      
        // Append total row
        rows.push([
          "",
          "",
          "",
          "",
          "",
          "Total",
          totalCharged.toFixed(2),
          totalDiscount.toFixed(2),
          totalCollected.toFixed(2),
          totalPending.toFixed(2),
        ]);
      
        autoTable(doc, {
          head: [columns],
          body: rows,
          startY: 45,
          styles: { fontSize: 10 },
          theme: "grid",
          headStyles: {
            fillColor: "#003366",
            textColor: "#fff",
            fontSize: 12,
          },
          bodyStyles: {
            fontSize: 10,
          },
        });
      
        doc.save(`OPD_${data[0].DoctorName}_${data[0].location}_${filters?.startDate}_to_${filters?.endDate}.pdf`);
        setIsLoading(false);
      };
      

    return (
        <div
            style={{
                padding: "20px",
                backgroundColor: "#f9fafb",
                borderRadius: "8px",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "15px",
                    marginBottom: "20px",
                }}
            >
                <img
                    src={exportIcon}
                    alt="excel"
                    style={{ width: "40px", height: "40px", cursor: "pointer" }}
                    title="Export to Excel"
                    onClick={handleExport}
                />

                <img
                    src={pdfIcon}
                    alt="pdf"
                    style={{ width: "40px", height: "40px", cursor: "pointer" }}
                    title="Export to PDF"
                    onClick={handleExportPDF}
                />
            </div>

            {/* Records per page selector */}
            <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "16px", fontWeight: "600" }}>
                    Records per page:{" "}
                </label>
                <select
                    onChange={handleRecordsPerPageChange}
                    value={recordsPerPage}
                    style={{
                        padding: "5px 10px",
                        borderRadius: "5px",
                        border: "1px solid #ddd",
                        fontSize: "16px",
                    }}
                >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={data?.length}>All</option>
                </select>
            </div>

            {/* Transactions Table */}
            <table style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "#fff",
                borderRadius: "8px",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
            }}>
                <thead>
                    <tr style={{ backgroundColor: "#003366", color: "#fff" }}>
                        <th style={{ padding: "12px 15px", textAlign: "left" }}>Date</th>
                        <th style={{ padding: "12px 15px", textAlign: "left" }}>Doctor Name</th>
                        <th style={{ padding: "12px 15px", textAlign: "left" }}>Referred Doctor</th>
                        <th style={{ padding: "12px 15px", textAlign: "left" }}>Patient Id</th>
                        <th style={{ padding: "12px 15px", textAlign: "left" }}>Patient Name</th>
                        <th style={{ padding: "12px 15px", textAlign: "left" }}>Location</th>
                        <th style={{ padding: "12px 15px", textAlign: 'right' }}>Charged</th>
                        <th style={{ padding: "12px 15px", textAlign: 'right' }}>Discount</th>
                        <th style={{ padding: "12px 15px", textAlign: 'right' }}>Collected</th>
                        <th style={{ padding: "12px 15px", textAlign: 'right' }}>Pending</th>
                    </tr>
                </thead>
                <tbody>
                    {currentRecords?.map((item, index) => (
                        <tr key={index} style={{
                            backgroundColor: index % 2 === 0 ? "#f9fafb" : "#d3dfeb", // Subtle dual-tone styling
                            transition: "background-color 0.3s ease",
                        }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.backgroundColor = "#e2e8f0")
                            }
                            onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor =
                                index % 2 === 0 ? "#f9fafb" : "#d3dfeb")
                            }>
                            <td style={{ padding: "12px 15px" }}>{item.Date.split('T')[0].split('-').reverse().join('-')}</td>
                            <td style={{ padding: "12px 15px" }}>{item.DoctorName}</td>
                            <td style={{ padding: "12px 15px" }}>{item.ReferredDoctorName}</td>
                            <td style={{ padding: "12px 15px" }}>{item.patientId}</td>
                            <td style={{ padding: "12px 15px" }}>{`${item.PatientName} (${item.patientId})`}</td>
                            <td style={{ padding: "12px 15px" }}>{item.location}</td>
                            <td style={{ padding: "12px 15px" }}>{item.amount + item.discount_amount}</td>
                            <td style={{ padding: "12px 15px" }}>{item.discount_amount}</td>
                            <td style={{ padding: "12px 15px" }}>{item.amount || 0}</td>
                            <td style={{ padding: "12px 15px" }}>
                                {0}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        style={{
                            padding: "8px 15px",
                            margin: "0 5px",
                            backgroundColor: "#003366",
                            color: "#fff",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        First
                    </button>
                    <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                            padding: "8px 15px",
                            margin: "0 5px",
                            backgroundColor: "#003366",
                            color: "#fff",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Previous
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => setCurrentPage(i + 1)}
                            disabled={currentPage === i + 1}
                            style={{
                                margin: '0 5px',
                                backgroundColor: currentPage === i + 1 ? '#003366' : '#fff',
                                color: currentPage === i + 1 ? '#fff' : '#000'
                            }}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                            padding: "8px 15px",
                            margin: "0 5px",
                            backgroundColor: "#003366",
                            color: "#fff",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Next
                    </button>
                    <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        style={{
                            padding: "8px 15px",
                            margin: "0 5px",
                            backgroundColor: "#003366",
                            color: "#fff",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Last
                    </button>
                </div>
            )}
        </div>
    );
}

export default OpdPaymentTables;